import React, {useState} from 'react';
import PropTypes from 'prop-types';
import uniqBy from 'lodash/uniqBy';
import isEqual from 'lodash/isEqual';
import {
  Dialog,
  DialogContent,
  DialogCommonActions,
  TextField,
  Button,
  ColorSwatch,
  Tooltip,
  InfoCircleIcon,
  IconButton,
  XClearCircleIcon,
  Typography
} from '@customink/pigment-react';
import {useDispatch, useSelector} from 'react-redux';
import {getUploadColors} from '~/selectors/CanvasSelectors';
import {getInkMatches} from '~/selectors/DesignSelectors';
import {updateInkMatches} from '~/actions/DesignActions';
import {metrics} from '~/vendor/metrics';
import {filterEmpty} from './util';

import {INK_MATCH_TYPES} from '~/constants/InkMatch';

import './index.scss';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties.
 * */
export default function InkMatchModal({open, onClose = () => {}, path}) {
  const dispatch = useDispatch();
  const uploadColors = useSelector(getUploadColors);
  const pantoneMatches = useSelector(getInkMatches);
  const [inkMatches, setInkMatches] = useState(pantoneMatches);

  const save = () => {
    metrics.setDimension('inkMatchCount', Object.keys(inkMatches).length);
    metrics.pageViewTag(path + '/success');
    const newInkMatches = filterEmpty(inkMatches);
    dispatch(updateInkMatches(newInkMatches));
    onClose();
  };

  const cancel = () => {
    metrics.pageViewTag(path + '/cancel');
    onClose();
  };

  const onChange = (e, color) => {
    setInkMatches({
      ...inkMatches,
      [color.hexString]: {
        value: e.target.value,
        type: INK_MATCH_TYPES.PANTONE,
        name: color.name
      }
    });
  };

  const uniqueColors = uniqBy(uploadColors, 'hexString');

  return (
    <Dialog
      open={open}
      onClose={cancel}
      className="ndx-InkMatch"
      mobileVariant="fullScreen"
      title="Let Us Know Your Pantone Brand Colors!"
      width="sm">
      <DialogContent>
        <Typography
          component="div"
          variant="paragraphDefault"
          className="ndx-InkMatch-subheader">
          Each Pantone match adds $20 to your total cost and you’ll get one free
          Pantone match for every $400 spent. While Pantone colors may not show
          accurately on the screen, each color will be matched to the Pantone
          color listed.
          <Tooltip
            arrow
            placement="top"
            size="small"
            title="Most Pantone colors can be matched, however we are unable to match uncoated or pastel colors.">
            <IconButton>
              <InfoCircleIcon fontSize="inheret" />
            </IconButton>
          </Tooltip>
        </Typography>
        <div className="ndx-InkMatch-body">
          {uniqueColors.map(color => (
            <div
              className="ndx-InkMatch-body-row"
              key={color.name}
              data-testid={`input-${color.name}`}>
              <div className="swatch-container">
                <ColorSwatch displayOnly color={`#${color.hexString}`} />
                {color.name}
              </div>
              <TextField
                label="Pantone Color"
                placeholder="7688 or Warm Gray 8"
                removeHelperTextSpace
                labelTooltipText="This Pantone color will be used for all elements of the same color in this design."
                TooltipProps={{placement: 'top', size: 'small'}}
                onChange={e => onChange(e, color)}
                value={inkMatches?.[color.hexString]?.value || ''}
                endAdornment={
                  inkMatches?.[color.hexString]?.value && (
                    <IconButton
                      className="ndx-InkMatch-clear"
                      aria-label="clear pantone color"
                      icon="close"
                      edge="end"
                      onClick={() => {
                        const newInkMatches = {...inkMatches};
                        delete newInkMatches[color.hexString];
                        setInkMatches(newInkMatches);
                      }}>
                      <XClearCircleIcon />
                    </IconButton>
                  )
                }
              />
            </div>
          ))}
        </div>
      </DialogContent>
      <DialogCommonActions className="ndx-InkMatch-actions">
        <Button
          disabled={isEqual(filterEmpty(inkMatches), pantoneMatches)}
          onClick={save}>
          Save & Continue
        </Button>
        <Button variant="secondary" onClick={cancel}>
          Cancel
        </Button>
      </DialogCommonActions>
    </Dialog>
  );
}

const propTypes = {
  open: PropTypes.bool,
  path: PropTypes.string,
  source: PropTypes.string,
  onClose: PropTypes.func
};

InkMatchModal.propTypes = propTypes;
